import { Icon } from 'design-system';
import {
  Badge,
  BasePagination,
  Box,
  Flex,
  HStack,
  IconButtonWithTooltip,
  PenIcon,
  PlusIcon,
  Stack,
  Text,
  useBreakpointValue,
} from 'design-system';
import { SupplierProfileDto, useGetSupplierProfilesApi } from 'apis/supplier-profiles';
import { BaseTableContainer, ExcelTable } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { CreateSupplierWizardModal } from 'pages/supplier-profiles/components/create-supplier-wizard-modal';
import { EditSupplierProfileModal, IEditSupplierProfileModalCell } from 'pages/supplier-profiles/components/edit-supplier-profile-modal';
import { ILinkSupplierModalCell, LinkSupplierModal } from 'pages/supplier-profiles/components/link-supplier-modal';
import {
  INotificationSettingsModalCell,
  NotificationSettingsModal,
} from 'pages/supplier-profiles/components/notification-settings-modal';
import { IUnlinkSupplierModalCell, UnlinkSupplierModal } from 'pages/supplier-profiles/components/unlink-supplier-modal';
import { NameFilterButton } from 'pages/supplier-profiles/components/name-filter-button';
import { TruncatedTextCell } from 'pages/supplier-profiles/components/truncated-text-cell';
import { formatCutOffTime } from 'pages/supplier-profiles/utils/format-cut-off-time';
import { mdiBellOutline, mdiLinkOff } from '@mdi/js';
import MdiIcon from '@mdi/react';
import React, { useEffect } from 'react';
import { FiLink } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

interface IColumnsAccessorData {
  id: number;
  supplierInfo: {
    supplierName: string | null;
    businessName: string;
    isLinked: boolean;
  };
  category: string | null;
  moq: string | null;
  delivery: string | null;
  paymentTerms: string | null;
  orderTerms: {
    cutOffTime: string | null;
    minimumOrder: number | null;
  };
  actions: SupplierProfileDto;
}

const CardLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.08em' textTransform='uppercase' color='gray.500'>
    {children}
  </Text>
);

const SupplierProfileCard = ({ item }: { item: Omit<IColumnsAccessorData, 'id'> }) => {
  const profile = item.actions;
  const { supplierName, businessName, isLinked } = item.supplierInfo;

  return (
    <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' p='14px' mb='10px'>
      <Box minW={0}>
        <HStack spacing='xs'>
          <Text fontWeight='semibold' fontSize='14px' color={supplierName ? 'gray.600' : 'gray.400'} noOfLines={1}>
            {supplierName ?? '—'}
          </Text>
          {isLinked && (
            <Badge colorScheme='green' flexShrink={0}>
              Linked
            </Badge>
          )}
        </HStack>
        <Text fontSize='12px' color='gray.500' noOfLines={1}>
          {businessName}
        </Text>
      </Box>

      <Flex gap='12px' mt='10px'>
        <Box flex='1' minW={0}>
          <CardLabel>Category</CardLabel>
          <Text fontSize='13px' noOfLines={2}>
            {item.category ?? '—'}
          </Text>
        </Box>
        <Box flex='1' minW={0}>
          <CardLabel>MOQ</CardLabel>
          <Text fontSize='13px' noOfLines={2}>
            {item.moq ?? '—'}
          </Text>
        </Box>
      </Flex>

      <Flex gap='12px' mt='10px'>
        <Box flex='1' minW={0}>
          <CardLabel>Delivery</CardLabel>
          <Text fontSize='13px' noOfLines={2}>
            {item.delivery ?? '—'}
          </Text>
        </Box>
        <Box flex='1' minW={0}>
          <CardLabel>Payment Terms</CardLabel>
          <Text fontSize='13px' noOfLines={2}>
            {item.paymentTerms ?? '—'}
          </Text>
        </Box>
      </Flex>

      <Box mt='10px'>
        <CardLabel>Cut-off / Min. Order</CardLabel>
        <Text fontSize='13px'>
          {item.orderTerms.cutOffTime || item.orderTerms.minimumOrder != null
            ? `${formatCutOffTime(item.orderTerms.cutOffTime)} / ${item.orderTerms.minimumOrder ?? '—'}`
            : '—'}
        </Text>
      </Box>

      <HStack spacing='4px' mt='8px'>
        <EditSupplierProfileModal
          profile={profile}
          trigger={
            <IconButtonWithTooltip
              size='sm'
              minW='42px'
              minH='42px'
              label='Edit'
              aria-label='edit-supplier-profile-btn'
              Icon={<PenIcon />}
              variant='unstyled'
            />
          }
        />
        <NotificationSettingsModal
          profile={profile}
          trigger={
            <IconButtonWithTooltip
              size='sm'
              minW='42px'
              minH='42px'
              label='Notification Settings'
              aria-label='notification-settings-btn'
              Icon={<MdiIcon path={mdiBellOutline} size={0.75} />}
              variant='unstyled'
            />
          }
        />
        {!profile.supplierId && (
          <LinkSupplierModal
            profile={profile}
            trigger={
              <IconButtonWithTooltip
                size='sm'
                minW='42px'
                minH='42px'
                label='Link Supplier'
                aria-label='link-supplier-btn'
                Icon={<Icon as={FiLink} color='green.500' />}
                variant='unstyled'
              />
            }
          />
        )}
        {!!profile.supplierId && (
          <UnlinkSupplierModal
            profile={profile}
            trigger={
              <IconButtonWithTooltip
                size='sm'
                minW='42px'
                minH='42px'
                label='Unlink Supplier'
                aria-label='unlink-supplier-btn'
                Icon={<MdiIcon path={mdiLinkOff} size={0.75} color='#E53E3E' />}
                variant='unstyled'
              />
            }
          />
        )}
      </HStack>
    </Box>
  );
};

export const SupplierProfilesListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isLoading, isFetching, data } = useGetSupplierProfilesApi();

  const { pageCount, total, data: profilesData } = data ?? {};

  useEffect(() => {
    dispatch(changeTitle('GPO Suppliers'));
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='Supplier' filterButtonComponent={<NameFilterButton />} />,
        accessor: 'supplierInfo',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplierInfo']>) => (
          <Stack spacing='0' maxW='220px'>
            <HStack spacing='xs'>
              <Text fontWeight='semibold' color={value.supplierName ? 'gray.600' : 'gray.400'} noOfLines={1}>
                {value.supplierName ?? '—'}
              </Text>
              <Badge colorScheme={value.isLinked ? 'green' : 'gray'} flexShrink={0}>
                {value.isLinked ? 'Linked' : 'Not Linked'}
              </Badge>
            </HStack>
            <Text fontSize='xs' color='gray.500' noOfLines={1}>
              {value.businessName}
            </Text>
          </Stack>
        ),
      },
      {
        Header: <TableHeader name='Category' />,
        accessor: 'category',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['category']>) => (
          <TruncatedTextCell value={value} maxW='140px' />
        ),
      },
      {
        Header: <TableHeader name='MOQ' />,
        accessor: 'moq',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['moq']>) => <TruncatedTextCell value={value} maxW='120px' />,
      },
      {
        Header: <TableHeader name='Delivery' />,
        accessor: 'delivery',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['delivery']>) => (
          <TruncatedTextCell value={value} maxW='160px' />
        ),
      },
      {
        Header: <TableHeader name='Payment Terms' />,
        accessor: 'paymentTerms',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['paymentTerms']>) => (
          <TruncatedTextCell value={value} maxW='160px' />
        ),
      },
      {
        Header: <TableHeader name='Cut-off / Min. Order' />,
        accessor: 'orderTerms',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['orderTerms']>) => (
          <TableCell
            value={
              value.cutOffTime || value.minimumOrder != null
                ? `${formatCutOffTime(value.cutOffTime)} / ${value.minimumOrder ?? '—'}`
                : '—'
            }
            whiteSpace='nowrap'
          />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <div style={{ display: 'flex', gap: '4px' }}>
            <CustomTableCell<IEditSupplierProfileModalCell>
              as={EditSupplierProfileModal}
              profile={value}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Edit'
                  aria-label='edit-supplier-profile-btn'
                  Icon={<PenIcon />}
                  variant='unstyled'
                />
              }
            />
            <CustomTableCell<INotificationSettingsModalCell>
              as={NotificationSettingsModal}
              profile={value}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Notification Settings'
                  aria-label='notification-settings-btn'
                  Icon={<MdiIcon path={mdiBellOutline} size={0.75} />}
                  variant='unstyled'
                />
              }
            />
            {!value.supplierId && (
              <CustomTableCell<ILinkSupplierModalCell>
                as={LinkSupplierModal}
                profile={value}
                trigger={
                  <IconButtonWithTooltip
                    size='sm'
                    label='Link Supplier'
                    aria-label='link-supplier-btn'
                    Icon={<Icon as={FiLink} color='green.500' />}
                    variant='unstyled'
                  />
                }
              />
            )}
            {!!value.supplierId && (
              <CustomTableCell<IUnlinkSupplierModalCell>
                as={UnlinkSupplierModal}
                profile={value}
                trigger={
                  <IconButtonWithTooltip
                    size='sm'
                    label='Unlink Supplier'
                    aria-label='unlink-supplier-btn'
                    Icon={<MdiIcon path={mdiLinkOff} size={0.75} color='#E53E3E' />}
                    variant='unstyled'
                  />
                }
              />
            )}
          </div>
        ),
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      (profilesData ?? []).map((profile) => ({
        id: profile.id,
        supplierInfo: {
          supplierName: profile.supplier?.name ?? null,
          businessName: profile.businessName,
          isLinked: !!profile.supplierId,
        },
        category: profile.category,
        moq: profile.moq,
        delivery: profile.delivery,
        paymentTerms: profile.paymentTerms,
        orderTerms: {
          cutOffTime: profile.supplier?.cutOffTime ?? null,
          minimumOrder: profile.supplier?.minimumOrder ?? null,
        },
        actions: profile,
      })),
    [profilesData]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='GPO Suppliers' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
          <CreateSupplierWizardModal
            trigger={<IconButtonWithTooltip label='Add supplier' aria-label='add-supplier-btn' Icon={<PlusIcon />} />}
          />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      {isMobile ? (
        // Cards live outside TableWrapper: TableContainer's whiteSpace='nowrap' would leak into card text.
        <Box flexGrow={1} overflowY='auto' minH='200px' px='sm' pb='sm'>
          <Flex display={{ base: 'flex', md: 'none' }} flexWrap='wrap' gap='4px' align='center' mb='10px'>
            <NameFilterButton />
          </Flex>
          {tableData.length === 0 ? (
            <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' p='14px'>
              <Text fontSize='13px' color='gray.500' textAlign='center'>
                No suppliers found
              </Text>
            </Box>
          ) : (
            tableData.map((item) => <SupplierProfileCard key={item.id} item={item} />)
          )}
        </Box>
      ) : (
        <BaseTableContainer.TableWrapper>
          <ExcelTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>
      )}

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
